import { useQuery } from "@tanstack/react-query";
import { api } from "../services/api";
import { Card, PageHeader } from "../components/ui";
import { getDisplayName } from "@hrhub/shared";

export function LeaderboardsPage() {
  const { data } = useQuery({
    queryKey: ["leaderboards"],
    queryFn: () =>
      api<{
        enabled: boolean;
        boards: {
          interviews: { username: string; nickname: string | null; globalName: string | null; monthInterviews: number }[];
          trainings: { username: string; nickname: string | null; globalName: string | null; monthTrainings: number }[];
          total: { username: string; nickname: string | null; globalName: string | null; monthInterviews: number; monthTrainings: number }[];
        };
      }>("/api/analytics/leaderboards"),
  });
  if (!data?.enabled) {
    return (
      <div>
        <PageHeader title="Leaderboards" />
        <Card>Leaderboards are currently disabled.</Card>
      </div>
    );
  }
  return (
    <div>
      <PageHeader title="Leaderboards" subtitle="Recognition metrics for hosted HR activity — not disciplinary rankings." />
      <div className="grid gap-4 lg:grid-cols-3">
        <Board title="Most Interviews This Month" rows={data.boards.interviews.map((m) => ({ name: getDisplayName(m), value: m.monthInterviews }))} />
        <Board title="Most Trainings This Month" rows={data.boards.trainings.map((m) => ({ name: getDisplayName(m), value: m.monthTrainings }))} />
        <Board title="Most Total Activity" rows={data.boards.total.map((m) => ({ name: getDisplayName(m), value: m.monthInterviews + m.monthTrainings }))} />
      </div>
    </div>
  );
}

function Board({ title, rows }: { title: string; rows: { name: string; value: number }[] }) {
  return (
    <Card>
      <h3 className="text-base font-semibold">{title}</h3>
      <ol className="mt-4 space-y-2">
        {rows.map((row, index) => (
          <li key={row.name} className="flex items-center justify-between gap-3 rounded-md bg-white/5 px-3 py-2 text-sm">
            <span>{index + 1}. {row.name}</span>
            <span className="font-semibold text-accent">{row.value}</span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

export function HeatmapPage() {
  const { data } = useQuery({
    queryKey: ["heatmap"],
    queryFn: () => api<{ days: { date: string; interviews: number; trainings: number; total: number }[] }>("/api/analytics/heatmap"),
  });
  const map = new Map((data?.days ?? []).map((d) => [d.date, d]));
  const days = Array.from({ length: 105 }).map((_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (104 - i));
    const key = date.toISOString().slice(0, 10);
    return { key, ...map.get(key) };
  });
  return (
    <div>
      <PageHeader title="Activity Calendar" subtitle="Hosted HR activity by day." />
      <Card>
        <div className="grid grid-cols-15 gap-1" style={{ gridTemplateColumns: "repeat(15, minmax(0, 1fr))" }}>
          {days.map((day) => {
            const total = day.total ?? 0;
            const opacity = total === 0 ? 0.08 : Math.min(1, 0.25 + total / 8);
            return (
              <div
                key={day.key}
                title={`${day.key} — ${day.interviews ?? 0} Interviews, ${day.trainings ?? 0} Trainings`}
                className="aspect-square rounded-md bg-accent"
                style={{ opacity }}
              />
            );
          })}
        </div>
      </Card>
    </div>
  );
}
